import { ComponentBase } from "../core/ComponentBase.js";

export class AgentConfigurationView extends ComponentBase {
    static styles = `
        .agent-config-container {
            display: flex;
            height: 100%;
            background-color: var(--bs-body-bg);
        }
        .config-sidebar {
            width: 260px;
            border-right: 1px solid var(--bs-border-color);
            display: flex;
            flex-direction: column;
            background-color: var(--bs-tertiary-bg);
        }
        .config-content-area {
            flex: 1;
            overflow-y: auto;
            padding: 2rem;
            position: relative;
        }
        .config-nav-item {
            padding: 12px 20px;
            cursor: pointer;
            border-left: 3px solid transparent;
            color: var(--bs-body-color);
            display: flex;
            align-items: center;
            gap: 10px;
            transition: all 0.2s;
        }
        .config-nav-item:hover {
            background-color: var(--bs-secondary-bg);
        }
        .config-nav-item.active {
            background-color: var(--bs-body-bg);
            border-left-color: var(--bs-primary);
            font-weight: 600;
            color: var(--bs-primary);
        }
        .config-header {
            padding: 1rem;
            border-bottom: 1px solid var(--bs-border-color);
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        .agent-config-tab-content {
            max-width: 800px;
            margin: 0 auto;
            animation: fadeIn 0.3s ease-in-out;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(5px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 768px) {
            .agent-config-container { flex-direction: column; }
            .config-sidebar { width: 100%; height: auto; flex-direction: column; border-right: none; border-bottom: 1px solid var(--bs-border-color); }
            .config-nav-list { flex-direction: row !important; overflow-x: auto; flex-wrap: nowrap; scrollbar-width: none; }
            .config-nav-list::-webkit-scrollbar { display: none; }
            .config-nav-item { white-space: nowrap; border-left: none; border-bottom: 3px solid transparent; }
            .config-nav-item.active { border-left: none; border-bottom-color: var(--bs-primary); }
            .config-content-area { padding: 1rem; }
        }
    `;

    constructor(config) {
        super(config);
        this.agentId = null;
        this.agentConfigData = {};
        this.originalConfigData = "{}";
        this.aiConfiguration = {};
        this.availableRoles = [];

        this.globalDataPromise = null;

        this.activeTabId = 'identity';
        this.agentListRoute = this.config.agentListRoute || '/user/settings/agents';

        // Playbooks tab removed — playbook capabilities are now part of the Skills & Capabilities tab.
        this.tabs = [
            { id: 'identity', label: 'Identity', icon: 'bi-person-badge', component: 'agent-config/AgentConfigIdentity' },
            { id: 'brain', label: 'Brain & Host', icon: 'bi-cpu', component: 'agent-config/AgentConfigBrain' },
            { id: 'personality', label: 'Personality', icon: 'bi-emoji-smile', component: 'agent-config/AgentConfigPersonality' },
            { id: 'voice', label: 'Voice & Phone', icon: 'bi-telephone', component: 'agent-config/AgentConfigVoice' },
            { id: 'skills', label: 'Capabilities', icon: 'bi-tools', component: 'agent-config/AgentConfigSkills' },
            { id: 'knowledgebase', label: 'Knowledge Base', icon: 'bi-database', component: 'agent-config/AgentConfigKnowledgeBase' },
            { id: 'memory', label: 'Memory', icon: 'bi-chat-square-dots', component: 'agent-config/AgentConfigMemory' },
        ];

        this.tabInstances = new Map();
        this.applyStyles();
    }

    async initAfterLogin() {
        this.globalDataPromise = this._loadGlobalData();
    }

    async _loadGlobalData() {
        try {
            const [aiConfig, roles] = await Promise.all([
                this.appClient.rpcClient.call('AIConfigRpc.getAIConfiguration'),
                this.appClient.rpcClient.call('RoleRpc.listRoles')
            ]);
            this.aiConfiguration = aiConfig || {};
            this.availableRoles = roles || [];
            this.log.info("Global configuration loaded for Agent Config View.");
        } catch (e) {
            this.log.error("Failed to load global config", e);
            this.appClient.notifier.error("Failed to load system configurations.");
        }
    }

    async refresh() {
        await this.loadDataBasedOnRoute();
    }

    async loadDataBasedOnRoute() {
        if (this.globalDataPromise) {
            await this.globalDataPromise;
        } else {
            await this._loadGlobalData();
        }

        const queryParams = this.appClient.layoutEngine.getQueryParams();
        this.agentId = queryParams.agentId;
        this.isCreateMode = !this.agentId;

        if (this.isCreateMode) {
            this.agentConfigData = this._getDefaultConfig();
        } else {
            try {
                this.agentConfigData = await this.appClient.rpcClient.call('AgentConfigRpc.findById', { _id: this.agentId });
                if (!this.agentConfigData) throw new Error("Agent not found");
                this._validateAndPatchModels();
            } catch (e) {
                this.appClient.notifier.error("Failed to load agent: " + e.message);
                this.handleBackNavigation();
                return;
            }
        }

        this.originalConfigData = JSON.stringify(this.agentConfigData);
        this.tabInstances.clear();
        this.activeTabId = 'identity';

        if (this.container) await this.render();
    }

    _validateAndPatchModels() {
        if (!this.aiConfiguration || !this.aiConfiguration.models) return;

        const validModelIds = Object.keys(this.aiConfiguration.models);
        const defaults = this.aiConfiguration.defaultModels || {};

        const checkAndReset = (field, defaultKey) => {
            const currentVal = this.agentConfigData[field];
            if (currentVal && !validModelIds.includes(currentVal)) {
                this.log.warn(`Agent config field '${field}' has invalid/deprecated model ID '${currentVal}'. Resetting.`);
                let newVal = defaults[defaultKey];
                if (!newVal || !validModelIds.includes(newVal)) newVal = null;
                this.agentConfigData[field] = newVal;
            }
        };

        checkAndReset('model', 'chat');
        checkAndReset('transcriptionModel', 'transcription');
        checkAndReset('ttsModel', 'tts');
        checkAndReset('openAIRealtimeModel', 'realtime');
        checkAndReset('geminiRealtimeModel', 'realtime');
    }

    _getDefaultConfig() {
        const defaults = this.aiConfiguration?.defaultModels || {};

        return {
            name: "",
            description: "",
            role: "Assistant",
            systemRole: "user",
            model: defaults.chat || "gpt-5.4-mini",
            hostService: null,
            contextScope: 'conversation',
            personalityEnabled: false,
            personality: { profession: 'general_assistant', style: 'neutral', verbosity: 'balanced' },
            capabilities: {
                sandbox: false,
                browserSandbox: false,
                vpcManagement: false,
                tasks: false,
                playbooks: false,
                scheduler: false,
                secrets: false,
                webApi: false,
                knowledgeBase: true,
                knowledgeBaseWebSearch: false,
                knowledgeBaseRemember: false,
                voiceEnabled: false,
                phoneEnabled: false,
                skills: true,
                commands: true,
                codeArtifacts: false,
                documentManagement: false,
            },
            tasks: { participate: false, manageBoards: false, allowedBoards: [] },
            playbooks: { canCreate: false, allowedTags: [] },
            scheduler: { scope: 'own' },
            secrets: { scope: 'agent' },
            webApi: { accessMode: 'all', allowedApis: [], blockedApis: [], manageRegistry: false },
            speechRealtime: { engineProvider: 'custom', voice: 'alloy' },
            telephony: { speakFirstOutgoing: 'agent_first', allowOutgoingCalls: false, allowIncomingCalls: false, allowGroupParticipation: false, forkNewInstanceForCalls: false },
            skills: { manageSkills: false, accessMode: 'all', allowed: [], blocked: [] },
            commands: { allowed: [], blocked: [], discoverableByDefault: false },
            documentManagement: { read: true, write: false, allowedCollections: [] },
            dynamicContext: { enabled: true, sections: {} },
            memory: {
                manager: 'default',
                compaction: {
                    enabled: true,
                    mode: 'threshold',
                    thresholdInputTokens: 100000,
                    targetSummaryTokens: 4000,
                    preserveRecentTurns: 20,
                    preserveToolGroups: true,
                    preserveHiddenContext: false,
                    manualAllowed: true
                }
            }
        };
    }

    isDirty() {
        const currentTab = this.tabInstances.get(this.activeTabId);
        if (currentTab && typeof currentTab.finalizeData === 'function') {
            currentTab.finalizeData();
        }
        return JSON.stringify(this.agentConfigData) !== this.originalConfigData;
    }

    isSystemAgent() {
        return this.agentConfigData.isSystemAgent === true || this.agentConfigData._source === 'file';
    }

    async handleBackNavigation() {
        if (this.isDirty()) {
            const confirmed = await this.appClient.notifier.confirm(this.t("You have unsaved changes. Are you sure you want to leave?"));
            if (!confirmed) {
                return;
            }
        }

        this.appClient.layoutEngine.navigate(this.agentListRoute);

    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'agent-config-container';
        }
        this.container.innerHTML = '';

        const sidebar = document.createElement('div');
        sidebar.className = 'config-sidebar';

        const header = document.createElement('div');
        header.className = 'config-header';

        const isSystem = this.isSystemAgent();
        const badge = isSystem ? `<span class="badge bg-info text-dark ms-2" style="font-size: var(--ab-text-2xs);">SYSTEM</span>` : '';

        header.innerHTML = `<h5 class="m-0 text-truncate d-flex align-items-center">${this.isCreateMode ? 'New Agent' : (this.agentConfigData.name || 'Unnamed Agent')} ${badge}</h5>`;
        sidebar.appendChild(header);

        const navList = document.createElement('div');
        navList.className = 'config-nav-list d-flex flex-column w-100';

        this.tabs.forEach(tab => {
            const item = document.createElement('div');
            item.className = `config-nav-item ${this.activeTabId === tab.id ? 'active' : ''}`;
            item.innerHTML = `<i class="bi ${tab.icon}"></i> ${this.t(tab.label)}`;
            item.onclick = () => this.switchTab(tab.id);
            navList.appendChild(item);
        });
        sidebar.appendChild(navList);

        const contentArea = document.createElement('div');
        contentArea.className = 'config-content-area';

        const toolbar = document.createElement('div');
        toolbar.className = 'd-flex justify-content-end mb-3 gap-2';

        const saveDisabled = isSystem ? 'disabled' : '';
        const saveTitle = isSystem ? 'System agents cannot be modified via UI' : 'Save Agent';

        toolbar.innerHTML = `
            <button class="btn btn-outline-secondary" id="btn-cancel">${this.t('Back')}</button>
            <button class="btn btn-primary" id="btn-save" ${saveDisabled} title="${saveTitle}"><i class="bi bi-save"></i> ${this.t('Save')}</button>
        `;
        contentArea.appendChild(toolbar);

        const tabContainer = document.createElement('div');
        tabContainer.id = 'active-tab-container';
        contentArea.appendChild(tabContainer);

        this.container.appendChild(sidebar);
        this.container.appendChild(contentArea);

        toolbar.querySelector('#btn-cancel').onclick = () => this.handleBackNavigation();
        if (!isSystem) {
            toolbar.querySelector('#btn-save').onclick = () => this.saveAgent();
        }

        await this.renderActiveTab(tabContainer);

        return this.container;
    }

    async switchTab(tabId) {
        if (this.activeTabId === tabId) return;

        const currentInstance = this.tabInstances.get(this.activeTabId);
        if (currentInstance && typeof currentInstance.finalizeData === 'function') {
            currentInstance.finalizeData();
        }

        this.activeTabId = tabId;

        const items = this.container.querySelectorAll('.config-nav-item');
        items.forEach((el, idx) => {
            if (this.tabs[idx].id === tabId) el.classList.add('active');
            else el.classList.remove('active');
        });

        const tabContainer = this.container.querySelector('#active-tab-container');
        await this.renderActiveTab(tabContainer);
    }

    async renderActiveTab(targetContainer) {
        targetContainer.innerHTML = '';

        const tabDef = this.tabs.find(t => t.id === this.activeTabId);
        if (!tabDef) return;

        let instance = this.tabInstances.get(tabDef.id);
        if (!instance) {
            const ComponentClass = await this.appClient.layoutEngine.componentManager.loadComponent(tabDef.component);
            if (ComponentClass) {
                instance = new ComponentClass({
                    appClient: this.appClient,
                    instanceId: `agent-cfg-${tabDef.id}-${this.agentId || 'new'}`,
                    agentConfig: this.agentConfigData,
                    aiConfig: this.aiConfiguration,
                    availableRoles: this.availableRoles,
                    isSystemAgent: this.isSystemAgent(),
                    // Thread component-level config into every tab so they can
                    // read flags like allowSystemCapabilities without coupling to
                    // the parent view directly.
                    componentConfig: this.config.componentConfig || {},
                });

                // --- CRITICAL FIX: Manually call initialize() ---
                await instance.initialize();

                this.tabInstances.set(tabDef.id, instance);
            } else {
                targetContainer.innerHTML = `<div class="alert alert-danger">Failed to load component: ${tabDef.component}</div>`;
                return;
            }
        }

        const el = await instance.render();
        targetContainer.appendChild(el);
    }

    async saveAgent() {
        if (this.isSystemAgent()) {
            this.appClient.notifier.warn("System agents cannot be modified.");
            return;
        }

        const currentInstance = this.tabInstances.get(this.activeTabId);
        if (currentInstance && typeof currentInstance.finalizeData === 'function') {
            currentInstance.finalizeData();
        }

        if (!this.agentConfigData.name) {
            this.appClient.notifier.error("Agent name is required.");
            return;
        }

        try {
            this.appClient.notifier.showProgress({ message: "Saving agent..." });

            let result;
            if (this.isCreateMode) {
                const currentProject = this.appClient.getSharedData('selectedProject');
                if (currentProject) this.agentConfigData.projectId = currentProject._id;

                result = await this.appClient.rpcClient.call('AgentConfigRpc.create', this.agentConfigData);
                this.appClient.notifier.hideProgress();
                this.appClient.notifier.success("Agent created successfully.");

                const newUrl = new URL(window.location);
                newUrl.searchParams.set('agentId', result._id);
                window.history.pushState({}, '', newUrl);

                this.agentId = result._id;
                this.isCreateMode = false;

                for (const key in this.agentConfigData) delete this.agentConfigData[key];
                Object.assign(this.agentConfigData, result);

                this.originalConfigData = JSON.stringify(this.agentConfigData);
                this.tabInstances.clear();
                this.render();
            } else {
                result = await this.appClient.rpcClient.call('AgentConfigRpc.update', this.agentConfigData);
                this.appClient.notifier.hideProgress();
                this.appClient.notifier.success("Agent updated successfully.");

                for (const key in this.agentConfigData) delete this.agentConfigData[key];
                Object.assign(this.agentConfigData, result);

                this.originalConfigData = JSON.stringify(this.agentConfigData);
            }
        } catch (error) {
            this.appClient.notifier.hideProgress();
            this.appClient.notifier.error(`Save failed: ${error.message}`);
        }
    }
}
